/**
 * The files Connect makes (2026-10-20): a PDF report or an Excel file, built here in the browser from the
 * document the API keeps (classes/Assistant/AssistantFiles.php: the words, the charts and every figure
 * behind them), so a file can be downloaded again for as long as it is kept, and no PDF or spreadsheet
 * library runs on the server. pdfmake and exceljs are loaded only when a file is downloaded.
 *
 * pdfDefinition() and fillWorkbook() are plain functions of the document (no browser needed), so the
 * output can be checked outside the panel too.
 */
import { chartSvg, chartSubtitle, compareText, cellText, figureList, hiddenRows, periodText, axisLabel, PRINT_PALETTE, TIME_AXES, TR_MONTH_NAMES } from "./assistantCharts";

const INK = { text: "#0f172a", muted: "#5b6678", rule: "#e2e8f0", strong: "#cbd5e1", head: "#f8fafc", brand: "#f97316" };
const PAGE_W = 612 - 2 * 48;   // US Letter, 48pt margins
const SVG_W = 680;              // a chart is drawn this wide and printed PAGE_W wide
const PDF_BAR_ROWS = 20;        // bars across in a report: the rest are in the table under the chart

/**
 * The day a file was made, as the stores saw it: the date the API wrote ("2026-10-20T16:59:19-07:00" is October 20
 * wherever the file is opened), in the file's language.
 */
function longDate(iso, lang) {
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  const d = day ? new Date(Date.UTC(+day[1], +day[2] - 1, +day[3])) : new Date(iso || Date.now());
  if (isNaN(d)) return "";
  if (lang === "tr") return day ? +day[3] + " " + TR_MONTH_NAMES[+day[2] - 1] + " " + day[1] : d.toLocaleDateString("tr-TR", { year: "numeric", month: "long", day: "numeric" });
  return d.toLocaleDateString("en-CA", day ? { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" } : { year: "numeric", month: "long", day: "numeric" });
}

/** The words a file is made of, in English and (for a Turkish conversation, 2026-10-28) in Turkish. */
const WORDS = {
  en: {
    report: "Report", total: "Total", sheet: (n) => "Sheet " + n, made: "Made with Connect",
    prepared: (by) => "Prepared by Connect" + (by ? " for " + by : ""),
    page: (page, pages) => "Page " + page + " of " + pages,
    firstBars: (shown, all) => "The chart shows the first " + shown + "; all " + all + " are in the table.",
  },
  tr: {
    report: "Rapor", total: "Toplam", sheet: (n) => "Sayfa " + n, made: "Connect ile hazırlandı",
    prepared: (by) => (by ? by + " için " : "") + "Connect tarafından hazırlandı",
    page: (page, pages) => "Sayfa " + page + " / " + pages,
    firstBars: (shown, all) => "Grafik ilk " + shown + " tanesini gösteriyor; " + all + " tanesinin hepsi tabloda.",
  },
};
const words = (lang) => (lang === "tr" ? WORDS.tr : WORDS.en);

/** A document's language: its own, else its first chart's or sheet's (the API marks Turkish ones "tr"); English otherwise. */
function docLanguage(doc) {
  if (doc.language) return doc.language;
  for (const s of doc.sections || []) for (const c of s.charts || []) if (c.language) return c.language;
  for (const sh of doc.sheets || []) if (sh.language) return sh.language;
  return "en";
}

// ------------------------------------------------------------------ PDF

/** Quiet rules between rows, stronger ones under the header and over the total; no lines down. */
const tableLayout = ({ rows, strong, head }) => ({
  hLineWidth: (i) => (i === 0 || i === rows ? 0 : strong.indexOf(i) !== -1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: (i) => (strong.indexOf(i) !== -1 ? INK.strong : INK.rule),
  fillColor: (row) => (head && row === 0 ? INK.head : null),
  paddingLeft: () => 5, paddingRight: () => 5, paddingTop: () => 3, paddingBottom: () => 3,
});

/** A chart's figures as a table: dates spelled out, money and counts right-aligned, the total in bold. */
function pdfTable(t, lang) {
  if (!t || !t.columns || !t.columns.length || !t.rows || !t.rows.length) return null;
  const list = figureList(t, lang);
  if (list) {
    // totals only: one figure a line
    return {
      table: { widths: ["auto", "auto"], body: list.map((f) => [{ text: f.label, color: INK.muted, margin: [0, 0, 24, 0] }, { text: f.text, alignment: "right", bold: true }]) },
      layout: tableLayout({ rows: list.length, strong: [], head: false }),
      fontSize: 9, margin: [0, 6, 0, 2],
    };
  }
  const cols = t.columns;
  const align = (c) => (c.unit === "text" ? "left" : "right");
  const body = [cols.map((c) => ({ text: c.label, style: "th", alignment: align(c) }))];
  t.rows.forEach((r) => body.push(r.map((v, i) => ({ text: cellText(v, cols[i], lang), alignment: align(cols[i]) }))));
  if (t.total) body.push(t.total.map((v, i) => ({ text: i === 0 ? words(lang).total : v === null ? "" : cellText(v, cols[i], lang), bold: true, alignment: align(cols[i]) })));
  return {
    // the header row repeats on every page and never stays behind alone at the foot of one
    table: { headerRows: 1, keepWithHeaderRows: 1, dontBreakRows: true, widths: cols.map((c, i) => (i === 0 ? "*" : "auto")), body },
    layout: tableLayout({ rows: body.length, strong: t.total ? [1, body.length - 1] : [1], head: true }),
    fontSize: cols.length > 6 ? 7.5 : 8.5,
    margin: [0, 6, 0, 2],
  };
}

/**
 * One chart in a report: its title and dates kept on one page with the picture (and with what leads into it:
 * the section's heading and words, when the chart opens the section), then every figure, then its notes.
 */
function pdfChart(c, lead, docLang) {
  const lang = c.language || docLang;
  const head = [{ text: c.title, style: "chartTitle" }];
  const sub = chartSubtitle(c, lang);
  if (sub) head.push({ text: sub, style: "chartSub" });
  // Drawn wider than the page and scaled down to it: the chart's 11px lettering prints at about 8pt.
  const svg = chartSvg(c, SVG_W, { palette: PRINT_PALETTE, font: "Roboto", maxRows: PDF_BAR_ROWS, height: 230 });
  const out = [];
  if (svg) {
    const picture = lead.concat([{ stack: head, margin: [0, lead.length ? 10 : 14, 0, 0] }, { svg, width: PAGE_W, margin: [0, 8, 0, 0] }]);
    if (hiddenRows(c, PDF_BAR_ROWS)) picture.push({ text: words(lang).firstBars(PDF_BAR_ROWS, c.labels.length), style: "note" });
    if (c.chart_note) picture.push({ text: c.chart_note, style: "note" });
    out.push({ stack: picture, unbreakable: true, margin: [0, 0, 0, 2] });
  } else {
    lead.forEach((n) => out.push(n));
    out.push({ stack: head, headlineLevel: 1, margin: [0, lead.length ? 10 : 14, 0, 0] });
  }
  const table = pdfTable(c.table, lang);
  if (table) out.push(table);
  [compareText(c, lang), c.note].filter(Boolean).forEach((text) => out.push({ text, style: "note" }));
  return out;
}

/** The pdfmake document of a report. */
export function pdfDefinition(doc) {
  const brand = doc.brand || "";
  const lang = docLanguage(doc);
  const w = words(lang);
  const content = [
    { text: (brand || "Connect").toUpperCase(), style: "brand" },
    { text: doc.title || w.report, style: "title" },
    { text: w.prepared(doc.made_by) + " · " + longDate(doc.made_at, lang), style: "meta" },
    { canvas: [{ type: "line", x1: 0, y1: 0, x2: PAGE_W, y2: 0, lineWidth: 1.5, lineColor: INK.brand }], margin: [0, 10, 0, 12] },
  ];
  if (doc.intro) content.push({ text: doc.intro, style: "intro" });
  (doc.sections || []).forEach((s) => {
    const lead = [];
    if (s.heading) lead.push({ text: s.heading, style: "h2", headlineLevel: 1 });
    if (s.text) lead.push({ text: s.text, style: "body" });
    const charts = s.charts || [];
    if (!charts.length) lead.forEach((n) => content.push(n));
    charts.forEach((c, i) => content.push(...pdfChart(c, i === 0 ? lead : [], lang)));
  });
  const footerText = (text, alignment) => ({ text, alignment, style: "footer" });
  return {
    pageSize: "LETTER",
    pageMargins: [48, 52, 48, 56],
    info: { title: doc.title || w.report, author: "Connect", creator: "Connect", subject: brand },
    defaultStyle: { font: "Roboto", fontSize: 10, lineHeight: 1.3, color: INK.text },
    styles: {
      brand: { fontSize: 8.5, bold: true, color: INK.brand, characterSpacing: 1.2 },
      title: { fontSize: 20, bold: true, margin: [0, 4, 0, 4], lineHeight: 1.15 },
      meta: { fontSize: 9, color: INK.muted },
      intro: { fontSize: 11, lineHeight: 1.4, margin: [0, 0, 0, 6] },
      h2: { fontSize: 13.5, bold: true, margin: [0, 16, 0, 4] },
      body: { margin: [0, 0, 0, 2] },
      chartTitle: { fontSize: 10.5, bold: true },
      chartSub: { fontSize: 8.5, color: INK.muted, margin: [0, 1, 0, 0] },
      th: { bold: true, color: INK.muted },
      note: { fontSize: 8, color: INK.muted, margin: [0, 3, 0, 0], lineHeight: 1.25 },
      footer: { fontSize: 8, color: INK.muted },
    },
    // A heading never ends a page alone: nothing of the page's content after it (the footer does not count).
    pageBreakBefore: (node, q) => node.headlineLevel === 1 && !q.getFollowingNodesOnPage().some((n) => n.style !== "footer"),
    footer: (page, pages) => ({
      columns: [footerText((brand ? brand + " · " : "") + w.made, "left"), footerText(w.page(page, pages), "right")],
      style: "footer",
      margin: [48, 22, 48, 0],
    }),
    content,
  };
}

// ------------------------------------------------------------------ Excel

const NUM_FMT = { money: '"$"#,##0.00;-"$"#,##0.00', count: "#,##0", percent: '0.0"%"' };
const GREY = { argb: "FF5B6678" };

/**
 * A cell as Excel keeps it: days and weeks as dates, figures as numbers (their format is the column's). In a Turkish
 * file the dates are written out in Turkish instead (Excel would name the months in its own language).
 */
function excelValue(v, col, lang) {
  if (v === null || v === undefined) return null;
  if (col.unit === "text") {
    const m = (col.key === "day" || col.key === "week") && /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v));
    if (m && lang === "tr") return axisLabel(v, col.key, true, "tr");
    return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : String(v);
  }
  const n = Number(v);
  return isFinite(n) ? n : String(v);
}

/** What a cell shows, to size its column. */
function shownText(v, col, lang) {
  if (v === null || v === undefined) return "";
  if (col.unit === "text") return TIME_AXES.indexOf(col.key) !== -1 ? axisLabel(v, col.key, true, lang) : String(v);
  return cellText(v, col, lang);
}

/** Excel takes a sheet name of up to 31 characters, none of : \ / ? * [ ]. */
function sheetName(name) {
  return String(name).replace(/[:\\/?*[\]]/g, " ").slice(0, 31);
}

/** A workbook with one sheet per chart: a title, the dates and stores, then the figures under a frozen, filterable header. */
export function fillWorkbook(ExcelJS, doc) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Connect";
  wb.created = new Date(doc.made_at || Date.now());
  const docLang = docLanguage(doc);
  (doc.sheets || []).forEach((sh, n) => {
    const lang = sh.language || docLang;
    const t = sh.table || { columns: [], rows: [] };
    const cols = t.columns || [];
    const ws = wb.addWorksheet(sh.name || (lang === "tr" ? sheetName(words(lang).sheet(n + 1)) : "Sheet " + (n + 1)));
    ws.addRow([sh.title || ""]).font = { bold: true, size: 14 };
    const sub = [periodText(sh.period, lang), sh.stores || ""].filter(Boolean).join(" · ");
    if (sub) ws.addRow([sub]).font = { color: GREY };
    if (sh.note) ws.addRow([sh.note]).font = { italic: true, color: GREY };
    ws.addRow([]);
    if (!cols.length) return;
    const list = figureList(t, lang);
    if (list) {
      // totals only: one figure a line, each in its own format
      list.forEach((f) => {
        const row = ws.addRow([f.label, f.value === null || f.value === undefined ? null : Number(f.value)]);
        row.getCell(2).numFmt = NUM_FMT[f.unit] || "General";
      });
      ws.getColumn(1).width = Math.min(48, Math.max(14, ...list.map((f) => f.label.length + 3)));
      ws.getColumn(2).width = Math.min(24, Math.max(12, ...list.map((f) => f.text.length + 3)));
      return;
    }

    const head = ws.addRow(cols.map((c) => c.label));
    head.font = { bold: true };
    head.eachCell((cell, i) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } };
      cell.border = { bottom: { style: "thin", color: { argb: "FFCBD5E1" } } };
      cell.alignment = { horizontal: cols[i - 1].unit === "text" ? "left" : "right" };
    });
    (t.rows || []).forEach((r) => ws.addRow(r.map((v, i) => excelValue(v, cols[i], lang))));
    const lastData = ws.rowCount;
    if (t.total) {
      const tr = ws.addRow(t.total.map((v, i) => (i === 0 ? words(lang).total : excelValue(v, cols[i], lang))));
      tr.font = { bold: true };
      tr.eachCell({ includeEmpty: true }, (cell) => { cell.border = { top: { style: "thin", color: { argb: "FFCBD5E1" } } }; });
    }

    cols.forEach((c, i) => {
      const col = ws.getColumn(i + 1);
      const fmt = c.unit === "text" ? (c.key === "day" ? "ddd mmm d, yyyy" : c.key === "week" ? "mmm d, yyyy" : null) : NUM_FMT[c.unit];
      const widest = Math.max(c.label.length, ...(t.rows || []).map((r) => shownText(r[i], c, lang).length), t.total ? shownText(t.total[i], c, lang).length : 0);
      col.width = Math.min(48, Math.max(i === 0 ? 14 : 10, widest + 3));
      if (fmt) for (let row = head.number + 1; row <= ws.rowCount; row++) ws.getCell(row, i + 1).numFmt = fmt;
    });
    ws.views = [{ state: "frozen", ySplit: head.number }];
    // The filter covers the figures, not the total under them, so sorting never moves the total.
    if (lastData > head.number) ws.autoFilter = { from: { row: head.number, column: 1 }, to: { row: lastData, column: cols.length } };
  });
  return wb;
}

// ------------------------------------------------------------------ in the browser

function save(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

async function pdfBlob(doc) {
  const [{ default: pdfMake }, { default: vfs }] = await Promise.all([import("pdfmake/build/pdfmake"), import("pdfmake/build/vfs_fonts")]);
  pdfMake.addVirtualFileSystem(vfs);
  return pdfMake.createPdf(pdfDefinition(doc)).getBlob();
}

async function xlsxBlob(doc) {
  const mod = await import("exceljs");
  const ExcelJS = mod.default || mod;
  const buf = await fillWorkbook(ExcelJS, doc).xlsx.writeBuffer();
  return new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

/** Build one of the files Connect made from its document (assistant/file) and save it under its name. */
export async function saveAssistantFile(file, doc) {
  const blob = file.kind === "pdf" ? await pdfBlob(doc) : await xlsxBlob(doc);
  save(blob, file.name);
}
